import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  listMeetings, createMeeting, proposeMeetingTimes, getAvailableSlots,
  cancelMeeting, getMeetingSummary, listContractors
} from '../lib/api';

const t = (lang, pl, en) => lang === 'pl' ? pl : en;

export default function Meetings({ lang, tenantId }) {
  const [meetings, setMeetings] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [summaryText, setSummaryText] = useState('');
  const [summaryMeetingId, setSummaryMeetingId] = useState(null);

  // Form
  const [form, setForm] = useState({
    contractor_id: '', datetime: '', duration_minutes: 30, title: '', location: 'online'
  });

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([listMeetings(30), listContractors(tenantId)]);
      setMeetings(m);
      setContractors(c);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleCreate() {
    if (!form.datetime) return toast.error(t(lang, 'Podaj datę', 'Provide date'));
    try {
      await createMeeting(form);
      toast.success(t(lang, 'Spotkanie utworzone', 'Meeting created'));
      setShowNew(false);
      loadData();
    } catch (e) { toast.error(e.message); }
  }

  async function handlePropose() {
    try {
      const result = await proposeMeetingTimes({ contractor_id: form.contractor_id, context: form.title });
      setProposals(result);
      toast.success(t(lang, 'AI zaproponowało terminy', 'AI proposed times'));
    } catch (e) { toast.error(e.message); }
  }

  async function handleCancel(id) {
    if (!window.confirm(t(lang, 'Anulować spotkanie?', 'Cancel meeting?'))) return;
    try {
      await cancelMeeting(id);
      toast.success(t(lang, 'Spotkanie anulowane', 'Meeting cancelled'));
      loadData();
    } catch (e) { toast.error(e.message); }
  }

  async function handleSummary(id) {
    try {
      const result = await getMeetingSummary(id, summaryText);
      toast.success(t(lang, 'Podsumowanie wygenerowane', 'Summary generated'));
      setSummaryMeetingId(null);
      loadData();
    } catch (e) { toast.error(e.message); }
  }

  const upcoming = meetings.filter(m => m.status !== 'cancelled' && new Date(m.datetime) >= new Date());
  const past = meetings.filter(m => m.status !== 'cancelled' && new Date(m.datetime) < new Date());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t(lang, 'Spotkania', 'Meetings')}</h1>
          <p className="text-muted-foreground text-sm">
            {t(lang, 'Planuj spotkania, AI proponuje terminy', 'Schedule meetings, AI proposes times')}
          </p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button>{t(lang, '+ Nowe Spotkanie', '+ New Meeting')}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t(lang, 'Zaplanuj Spotkanie', 'Schedule Meeting')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder={t(lang, 'Tytuł', 'Title')} value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })} />
              <Select value={form.contractor_id} onValueChange={v => setForm({ ...form, contractor_id: v })}>
                <SelectTrigger><SelectValue placeholder={t(lang, 'Kontrahent', 'Contractor')} /></SelectTrigger>
                <SelectContent>
                  {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="datetime-local" value={form.datetime}
                onChange={e => setForm({ ...form, datetime: e.target.value })} />
              <div className="flex gap-2">
                <Input type="number" placeholder={t(lang, 'Czas trwania (min)', 'Duration (min)')}
                  value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })} />
                <Select value={form.location} onValueChange={v => setForm({ ...form, location: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online (Google Meet)</SelectItem>
                    <SelectItem value="office">{t(lang, 'Biuro', 'Office')}</SelectItem>
                    <SelectItem value="client_site">{t(lang, 'U klienta', 'Client site')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} className="flex-1">{t(lang, 'Utwórz', 'Create')}</Button>
                <Button variant="outline" onClick={handlePropose}>{t(lang, 'AI Zaproponuj', 'AI Propose')}</Button>
              </div>

              {proposals && (
                <div className="border rounded p-3 space-y-2">
                  <div className="text-sm font-medium">{t(lang, 'Propozycje AI:', 'AI Proposals:')}</div>
                  {(proposals.proposals || proposals.slots || []).map((p, i) => (
                    <Button key={i} variant="outline" size="sm" className="w-full justify-start"
                      onClick={() => setForm({ ...form, datetime: p.datetime || p.start })}>
                      {p.datetime || p.start} — {p.reason || p.label || ''}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Upcoming */}
      <div>
        <h2 className="text-lg font-semibold mb-3">{t(lang, 'Nadchodzące', 'Upcoming')} ({upcoming.length})</h2>
        <div className="space-y-2">
          {upcoming.map(m => (
            <Card key={m.id}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{m.title}</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(m.datetime).toLocaleString(lang === 'pl' ? 'pl-PL' : 'en-US')}
                      {' '} | {m.duration_minutes} min | {m.location}
                    </div>
                    {m.contractor_name && <div className="text-sm">{m.contractor_name}</div>}
                  </div>
                  <div className="flex gap-2">
                    {m.meeting_link && (
                      <Button size="sm" variant="outline" onClick={() => window.open(m.meeting_link)}>
                        {t(lang, 'Dołącz', 'Join')}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleCancel(m.id)}>
                      {t(lang, 'Anuluj', 'Cancel')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {upcoming.length === 0 && (
            <p className="text-muted-foreground text-sm">{t(lang, 'Brak nadchodzących spotkań', 'No upcoming meetings')}</p>
          )}
        </div>
      </div>

      {/* Past */}
      <div>
        <h2 className="text-lg font-semibold mb-3">{t(lang, 'Przeszłe', 'Past')} ({past.length})</h2>
        <div className="space-y-2">
          {past.slice(0, 10).map(m => (
            <Card key={m.id}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{m.title}</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(m.datetime).toLocaleString(lang === 'pl' ? 'pl-PL' : 'en-US')}
                      {m.contractor_name && ` | ${m.contractor_name}`}
                    </div>
                    {m.summary && <p className="text-sm mt-1">{m.summary}</p>}
                  </div>
                  <div>
                    {!m.summary && (
                      summaryMeetingId === m.id ? (
                        <div className="flex gap-1">
                          <Textarea placeholder={t(lang, 'Notatki ze spotkania...', 'Meeting notes...')}
                            value={summaryText} onChange={e => setSummaryText(e.target.value)} className="w-48 h-16" />
                          <Button size="sm" onClick={() => handleSummary(m.id)}>OK</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setSummaryMeetingId(m.id)}>
                          {t(lang, 'Podsumowanie AI', 'AI Summary')}
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
